import { fabric } from 'fabric';
import _ from 'lodash';

import { ANNOTATION_TYPE, DEFAULT_FONT, DEFAULT_FONT_BOLD } from '@/shared/ui';
import {
  convertBeatTypeNumberToText,
  formatHesBeatStatus,
  isDeleteBeatMode,
} from '@/shared/ui/components/FabricJS/Helper';

type BeatMarkerEntry = {
  rectangle: fabric.Rect;
  triangle: fabric.Triangle;
  text: fabric.Text;
  originType: string;
  beatSample: number;
};

type BeatMarkersCtor = new (
  canvas: fabric.Canvas,
  beatMarkers: number[],
  verticalChannelPlacement: number[],
  config: any,
  isAppliedAI: boolean,
  isShowVES: boolean,
  isShowSVES: boolean,
  hesBeatStatus: number[],
  aiData: any,
  handleOpenSnackbar: (message: string) => void,
) => any;

let BeatMarkersImpl: any;

(function () {
  const BEAT_MAX_DISTANCE = 45;
  const PRINTABLE_BEAT_COLOR: Record<string, string> = {
    N: '#1D2F47',
    S: '#E51C22',
    V: '#E05E04',
    Q: '#5637FA',
  };
  const NORMAL_BEAT_COLOR: Record<string, string> = {
    N: '#6D7B8F',
    S: '#FA5A5F',
    V: '#FA893C',
    Q: '#846EFA',
  };
  const UNHOVER_BEAT_COLOR: Record<string, string> = {
    N: '#9DA7B2',
    S: '#E08789',
    V: '#E09663',
    Q: '#A097CC',
  };
  const HOVER_BEAT_COLOR: Record<string, string> = {
    N: '#364152',
    S: '#E51C22',
    V: '#E05E04',
    Q: '#5637FA',
  };
  const NORMAL_TEXT_COLOR = '#1D2F47';
  const ARBEAT_TEXT_COLOR = '#4B5B73';

  const FONT = {
    fontFamily: DEFAULT_FONT,
    fontSize: 12,
    fontWeight: 100,
    selectable: false,
    hasControls: false,
    hasBorders: false,
    hasRotatingPoint: false,
    selection: false,
    skipTargetFind: false,
    editable: false,
  };

  BeatMarkersImpl = function (
    canvas: fabric.Canvas,
    beatMarkers: number[] = [],
    verticalChannelPlacement: number[],
    config: any,
    isAppliedAI: boolean,
    isShowVES: boolean,
    isShowSVES: boolean,
    hesBeatStatus: number[] = [],
    aiData: any,
    handleOpenSnackbar: (message: string) => void,
  ) {
    this.canvas = canvas;
    this.type = ANNOTATION_TYPE.BEAT_MARKER;
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.config = config;
    this.bpmArray = [];
    this.arrayPrintableBeatMarker = [];
    this.arrayBeatMarker = [];
    this.sizeBeatMarker = 0;
    this.arrayLabel = [];
    this.sizeLabel = 0;
    this.isVisibility = true;
    this.isAppliedAI = isAppliedAI;
    this.beatMarkers = [...beatMarkers];
    this.originalHesBeatStatus = [...hesBeatStatus];
    this.currentHesBeatStatus = [...hesBeatStatus];
    this.isShowVES = isShowVES;
    this.isShowSVES = isShowSVES;
    this.aiData = aiData;
    this.hoverBeat = null;
    this.selectedBeats = [];
    this.isSelectingBeat = false;
    this.handleOpenSnackbar = handleOpenSnackbar;
    this.normalColor = NORMAL_BEAT_COLOR;
    this.textColor = NORMAL_TEXT_COLOR;
  };
  BeatMarkersImpl.prototype.updateARBeatMode = function (isEnter: boolean) {
    this.normalColor = isEnter ? UNHOVER_BEAT_COLOR : NORMAL_BEAT_COLOR;
    this.textColor = isEnter ? ARBEAT_TEXT_COLOR : NORMAL_TEXT_COLOR;
  };

  BeatMarkersImpl.prototype.update = function (
    beatMarkers: number[] = [],
    verticalChannelPlacement: number[],
    config: any,
    isAppliedAI: boolean,
    isShowVES: boolean,
    isShowSVES: boolean,
    hesBeatStatus: number[] = [],
    aiData: any,
  ) {
    this.bpmArray.length = 0;
    this.beatMarkers = [...beatMarkers];
    this.originalHesBeatStatus = [...hesBeatStatus];
    this.currentHesBeatStatus = [...hesBeatStatus];
    this.isAppliedAI = isAppliedAI;
    this.isShowVES = isShowVES;
    this.isShowSVES = isShowSVES;
    this.aiData = aiData;
    if (!isShowVES) {
      this.changeHesBeatStatus(60);
    }
    if (!isShowSVES) {
      this.changeHesBeatStatus(70);
    }
    this.verticalChannelPlacement = verticalChannelPlacement;
    this.config = config;
  };

  BeatMarkersImpl.prototype.render = function () {
    this.canvas.renderAll();
  };

  BeatMarkersImpl.prototype.drawPrintable = function (offsetX: number) {
    if (this.config.speed !== 2.5) {
      this.drawBeatMarkerLabels(offsetX);
      this.drawPrintableBeatMarker(offsetX);
    }
  };

  BeatMarkersImpl.prototype.removePrintable = function () {
    this.arrayPrintableBeatMarker.forEach((text: fabric.Text) => {
      this.canvas.remove(text);
    });
    this.arrayPrintableBeatMarker.length = 0;
  };

  BeatMarkersImpl.prototype.draw = function (offsetX: number, isInViewAllMode: boolean) {
    if (!isInViewAllMode && this.config.speed !== 2.5) {
      this.drawBeatMarkerLabels(offsetX);
      this.drawBeatMarker(offsetX);
    }
  };

  BeatMarkersImpl.prototype.drawPrintableBeatMarker = function (offsetX: number) {
    const { numberOfVisibleChannels, width } = this.config;
    const beatMarkersLength = this.beatMarkers.length;
    for (let i = 0; i < beatMarkersLength; i += 1) {
      let type = 'N';
      if (this.isAppliedAI) {
        type = convertBeatTypeNumberToText(this.currentHesBeatStatus[i]);
      }
      const xPos = this.indexToPixOffset(this.beatMarkers[i], offsetX);
      const yPos = 5;
      // *: Beat marker should be drawn no more than BEAT_MAX_DISTANCE away from lower baseline
      const minYPos =
        this.verticalChannelPlacement[numberOfVisibleChannels - 1] + BEAT_MAX_DISTANCE;
      const yPosAdjusted = Math.min(yPos, minYPos);
      if (xPos > 0 && xPos < width) {
        const text = new fabric.Text(type, {
          ...FONT,
          fontFamily: DEFAULT_FONT_BOLD,
          fontSize: 16,
          width: 12,
          top: yPosAdjusted - 4,
          hoverCursor: 'pointer',
          fill: PRINTABLE_BEAT_COLOR[type],
          fontWeight: 700,
        });
        text.left = xPos - text.width / 2;
        this.arrayPrintableBeatMarker.push(text);
        this.canvas.add(text);
      }
    }
  };

  BeatMarkersImpl.prototype.drawBeatMarker = function (offsetX: number) {
    const { numberOfVisibleChannels } = this.config;
    this.sizeBeatMarker = 0;
    const beatMarkersLength = this.beatMarkers.length;
    for (let i = 0; i < beatMarkersLength; i += 1) {
      let type = 'N';
      if (this.isAppliedAI) {
        type = convertBeatTypeNumberToText(this.currentHesBeatStatus[i]);
      }

      const xPos = this.indexToPixOffset(this.beatMarkers[i], offsetX);
      const yPos = 5;

      // *: Beat marker should be drawn no more than BEAT_MAX_DISTANCE away from lower baseline
      const minYPos =
        this.verticalChannelPlacement[numberOfVisibleChannels - 1] + BEAT_MAX_DISTANCE;
      const yPosAdjusted = Math.min(yPos, minYPos);

      this.drawTriangle(xPos, yPosAdjusted, this.sizeBeatMarker, type, this.beatMarkers[i]);
    }
    const arrayBeatMarkerLength = this.arrayBeatMarker.length;
    for (let i = this.sizeBeatMarker; i < arrayBeatMarkerLength; i += 1) {
      this.arrayBeatMarker[i].text.visible = false;
      this.arrayBeatMarker[i].triangle.visible = false;
      this.arrayBeatMarker[i].rectangle.visible = false;
    }
  };

  BeatMarkersImpl.prototype.drawBeatMarkerLabels = function (offsetX: number) {
    const { numberOfVisibleChannels } = this.config;
    this.sizeLabel = 0;
    const beatMarkersLength = this.beatMarkers.length;
    for (let i = 0; i < beatMarkersLength - 1; i += 1) {
      const measurementText = this.getBeatMarkerLabel(this.beatMarkers[i], this.beatMarkers[i + 1]);
      const x1Px = this.indexToPixOffset(this.beatMarkers[i], offsetX);
      const x2Px = this.indexToPixOffset(this.beatMarkers[i + 1], offsetX);
      const yPx = 2;

      // *: Beat marker should be drawn no more than BEAT_MAX_DISTANCE away from lower baseline
      const minYPx = this.verticalChannelPlacement[numberOfVisibleChannels - 1] + BEAT_MAX_DISTANCE;
      const yPxAdjusted = Math.min(yPx, minYPx);
      this.drawText(measurementText, x1Px, x2Px, yPxAdjusted, this.sizeLabel);
    }
    const arrayLabelLength = this.arrayLabel.length;
    for (let i = this.sizeLabel; i < arrayLabelLength; i += 1) {
      this.arrayLabel[i].visible = false;
    }
  };

  BeatMarkersImpl.prototype.getBeatMarkerLabel = function (
    sampleA: number,
    sampleB: number,
    hasLabel = true,
  ) {
    const { samplingRate } = this.config;
    const difference = Math.abs(sampleA - sampleB);
    const miliSec = (difference * 1000) / samplingRate;
    const bpm = 60 / (miliSec / 1000);
    this.bpmArray.push(Math.round(bpm));
    return hasLabel ? `${Math.round(miliSec)}\n${Math.round(bpm)}` : bpm;
  };

  BeatMarkersImpl.prototype.getMinMaxHr = function () {
    return {
      minHr: _.min(this.bpmArray),
      maxHr: _.max(this.bpmArray),
    };
  };

  BeatMarkersImpl.prototype.drawText = function (
    measurementText: string,
    x1Px: number,
    x2Px: number,
    yPxAdjusted: number,
    i: number,
  ) {
    const { width } = this.config;
    const left = (x1Px + x2Px) / 2;
    if (left > -measurementText.length * 7 && left < width) {
      this.sizeLabel += 1;
      if (i >= this.arrayLabel.length) {
        const text = new fabric.Text(measurementText, {
          ...FONT,
          fontSize: 12,
          left,
          top: yPxAdjusted,
          textAlign: 'justify-center',
          originX: 'center',
          lineHeight: 0.9,
          visible: this.isVisibility,
          fill: this.textColor,
        });
        text.type = 'text';
        this.arrayLabel.push(text);
        this.canvas.add(text);
      } else {
        this.arrayLabel[i].set({
          visible: this.isVisibility,
          left,
          top: yPxAdjusted,
          text: measurementText,
          fill: this.textColor,
        });
        this.arrayLabel[i].setCoords();
      }
    }
  };

  BeatMarkersImpl.prototype.drawTriangle = function (
    startX: number,
    startY: number,
    i: number,
    type: string,
    beatSample: number,
  ) {
    const { width } = this.config;
    if (startX > 0 && startX < width) {
      this.sizeBeatMarker += 1;
      if (i >= this.arrayBeatMarker.length) {
        const rectangle = new fabric.Rect({
          ...FONT,
          // `fontSize` isn't in @types/fabric's IRectOptions but fabric.Rect accepts it at runtime
          ...({ fontSize: 12 } as any),
          width: 9,
          height: 11,
          top: startY,
          left: startX - 4,
          hoverCursor: 'pointer',
          strokeWidth: 0,
          visible: this.isVisibility,
          fill: this.normalColor[type],
          borderColor: this.normalColor[type],
          rx: 1,
          ry: 1,
          excludeFromExport: true, // Print compatible beat marker will take place when export
        });

        const triangle = new fabric.Triangle({
          ...FONT,
          // `fontSize` isn't in @types/fabric's ITriangleOptions but fabric.Triangle accepts it at runtime
          ...({ fontSize: 12 } as any),
          width: 9,
          height: 4,
          top: startY + 11,
          left: startX - 4,
          hoverCursor: 'pointer',
          flipY: true,
          strokeWidth: 0,
          visible: this.isVisibility,
          fill: this.normalColor[type],
          borderColor: this.normalColor[type],
          excludeFromExport: true,
        });

        const text = new fabric.Text(type, {
          ...FONT,
          fontSize: 10,
          width: 5,
          top: startY + 1,
          hoverCursor: 'pointer',
          fill: 'white',
          visible: this.isVisibility,
          excludeFromExport: true,
        });
        // *: Card center = startX + 0.5; nudge the label so its right-heavy glyph ink reads centered.
        text.left = startX - text.width / 2;
        this.arrayBeatMarker.push({
          rectangle,
          triangle,
          text,
          originType: type,
          beatSample,
        });
        this.canvas.add(rectangle, triangle, text);
      } else {
        if (this.isAppliedAI) {
          if (type !== this.arrayBeatMarker[i].text.text && !this.isSelectingBeat) {
            this.arrayBeatMarker[i].text.set({ text: type });
          }
        }
        this.arrayBeatMarker[i].rectangle.set({
          visible: this.isVisibility,
          top: startY,
          left: startX - 4,
          fill: this.normalColor[type],
          borderColor: this.normalColor[type],
        });

        this.arrayBeatMarker[i].triangle.set({
          visible: this.isVisibility,
          top: startY + 11,
          left: startX - 4,
          fill: this.normalColor[type],
          borderColor: this.normalColor[type],
        });

        this.arrayBeatMarker[i].text.set({
          visible: this.isVisibility,
          top: startY + 1,
          left: startX - this.arrayBeatMarker[i].text.width / 2,
        });

        this.arrayBeatMarker[i].originType = type;
        this.arrayBeatMarker[i].beatSample = beatSample;
      }
    }
  };

  BeatMarkersImpl.prototype.deselectBeat = function () {
    this.isSelectingBeat = false;
    this.selectedBeats.forEach((beat: BeatMarkerEntry) => {
      beat.triangle.set({
        fill: this.normalColor[beat.originType],
        borderColor: this.normalColor[beat.originType],
      });
      beat.rectangle.set({
        fill: this.normalColor[beat.originType],
        borderColor: this.normalColor[beat.originType],
      });
      beat.text.set({
        text: beat.originType,
      });
    });
    this.selectedBeats = [];
  };

  BeatMarkersImpl.prototype.handleSelectBeat = function ({
    startSample,
    endSample,
    beatTypeNumber,
  }: {
    startSample: number;
    endSample: number;
    beatTypeNumber: number;
  }) {
    this.isSelectingBeat = true;
    this.selectedBeats.forEach((beat: BeatMarkerEntry) => {
      beat.triangle.set({
        fill: this.normalColor[beat.originType],
        borderColor: this.normalColor[beat.originType],
      });
      beat.rectangle.set({
        fill: this.normalColor[beat.originType],
        borderColor: this.normalColor[beat.originType],
      });
      beat.text.set({
        text: beat.originType,
      });
    });
    const isDelete = isDeleteBeatMode(beatTypeNumber);
    const selectedBeatsSample = this.beatMarkers.filter(
      (beatSample: number) => beatSample >= startSample && beatSample <= endSample,
    );
    const selectedBeatMarker = this.arrayBeatMarker.filter((beat: BeatMarkerEntry) =>
      selectedBeatsSample.includes(beat.beatSample),
    );
    const type = convertBeatTypeNumberToText(beatTypeNumber);
    selectedBeatMarker.forEach((beat: BeatMarkerEntry) => {
      beat.triangle.set({
        fill: HOVER_BEAT_COLOR[isDelete ? beat.originType : type],
        borderColor: HOVER_BEAT_COLOR[isDelete ? beat.originType : type],
      });
      beat.rectangle.set({
        fill: HOVER_BEAT_COLOR[isDelete ? beat.originType : type],
        borderColor: HOVER_BEAT_COLOR[isDelete ? beat.originType : type],
      });
      if (!isDelete) {
        beat.text.set({
          text: type,
        });
      }
    });
    this.selectedBeats = selectedBeatMarker;
    this.render();
  };

  BeatMarkersImpl.prototype.resetHoverBeat = function () {
    if (this.beatHover) {
      this.beatHover.rectangle.set({
        fill: this.normalColor[this.beatHover.originType],
        borderColor: this.normalColor[this.beatHover.originType],
      });
      this.beatHover.triangle.set({
        fill: this.normalColor[this.beatHover.originType],
        borderColor: this.normalColor[this.beatHover.originType],
      });
    }
  };

  BeatMarkersImpl.prototype.handleHoverBeat = function (
    pointer: { x: number; y: number },
    offsetX: number,
    isDelete: boolean,
  ) {
    if (_.isEmpty(pointer)) {
      return;
    }
    const selectedBeatMarkerObj = this.checkBeatAtPosition(pointer, offsetX);
    const isHoverBeat = !_.isEmpty(selectedBeatMarkerObj);
    if (isHoverBeat) {
      if (this.beatHover) {
        this.beatHover.rectangle.set({
          fill: this.normalColor[this.beatHover.originType],
          borderColor: this.normalColor[this.beatHover.originType],
        });
        this.beatHover.triangle.set({
          fill: this.normalColor[this.beatHover.originType],
          borderColor: this.normalColor[this.beatHover.originType],
        });
      }
      this.beatHover = selectedBeatMarkerObj.selectedBeatMarker;
      this.beatHover.rectangle.set({
        fill: HOVER_BEAT_COLOR[this.beatHover.originType],
        borderColor: HOVER_BEAT_COLOR[this.beatHover.originType],
      });
      this.beatHover.triangle.set({
        fill: HOVER_BEAT_COLOR[this.beatHover.originType],
        borderColor: HOVER_BEAT_COLOR[this.beatHover.originType],
      });
      this.canvas.bringToFront(this.beatHover.rectangle);
      this.canvas.bringToFront(this.beatHover.triangle);
      this.canvas.bringToFront(this.beatHover.text);
    } else if (this.beatHover) {
      this.beatHover.rectangle.set({
        fill: this.normalColor[this.beatHover.originType],
        borderColor: this.normalColor[this.beatHover.originType],
      });
      this.beatHover.triangle.set({
        fill: this.normalColor[this.beatHover.originType],
        borderColor: this.normalColor[this.beatHover.originType],
      });
    }
    this.render();
  };

  BeatMarkersImpl.prototype.addBeat = function (
    clickedOffsetX: number,
    offsetX: number,
    beatTypeNumber: number,
  ) {
    // *: Check for adding duplicate beat which caused infinity bpm value
    const isAdded = _.find(
      this.beatMarkers,
      (x) =>
        this.indexToPixOffset(clickedOffsetX, offsetX) >= this.indexToPixOffset(x, offsetX) - 8 &&
        this.indexToPixOffset(clickedOffsetX, offsetX) <= this.indexToPixOffset(x, offsetX) + 8,
    ); // *: 8 is width of rectangle
    if (!_.includes(this.beatMarkers, clickedOffsetX) && !isAdded) {
      // *: New code
      const addIndex = _.findIndex(
        this.beatMarkers,
        (x: number, index: number) =>
          (clickedOffsetX > x && clickedOffsetX < this.beatMarkers[index + 1]) ||
          (clickedOffsetX > x && !this.beatMarkers[index + 1]),
      );
      if (addIndex >= 0) {
        this.beatMarkers.splice(addIndex + 1, 0, clickedOffsetX);
        this.originalHesBeatStatus.splice(addIndex + 1, 0, beatTypeNumber);
        this.currentHesBeatStatus.splice(addIndex + 1, 0, beatTypeNumber);
      } else {
        this.beatMarkers.unshift(clickedOffsetX);
        this.originalHesBeatStatus.unshift(beatTypeNumber);
        this.currentHesBeatStatus.unshift(beatTypeNumber);
      }
      this.handleOpenSnackbar(`${convertBeatTypeNumberToText(beatTypeNumber)}-beat added`);
      // *: Old code
      // this.beatMarkers.push(clickedOffsetX);
      // this.beatMarkers = this.beatMarkers.sort((a, b) => a - b); // *: Order array by asc
      // const addedIndex = _.findIndex(this.beatMarkers, x => x === clickedOffsetX);
      // if (addedIndex > -1 && this.isAppliedAI) {
      //   this.originalHesBeatStatus.splice(addedIndex, 0, 99);
      //   this.currentHesBeatStatus.splice(addedIndex, 0, 99);
      // }
      this.reDrawBeatMarkers(offsetX, false);
      this.render();
    }
  };

  BeatMarkersImpl.prototype.delete = function (pointer: { x: number; y: number }, offsetX: number) {
    // *: Find beat marker with point
    const beatMarker = _.find(
      this.arrayBeatMarker,
      (beat: BeatMarkerEntry) =>
        beat.rectangle.visible &&
        beat.triangle.visible &&
        beat.text.visible &&
        ((pointer.x >= beat.rectangle.left &&
          pointer.x <= beat.rectangle.left + beat.rectangle.width &&
          pointer.y >= beat.rectangle.top &&
          pointer.y <= beat.rectangle.top + beat.rectangle.height) ||
          (pointer.x >= beat.triangle.left &&
            pointer.x <= beat.triangle.left + beat.triangle.width &&
            pointer.y >= beat.triangle.top &&
            pointer.y <= beat.triangle.top + beat.triangle.height)),
    ); // *: Click rectangle or triangle

    if (beatMarker) {
      const x = Math.round((beatMarker.rectangle.left + 4) / this.config.indexToPix + offsetX);

      // *: Find index of above beat marker
      const index = _.findLastIndex(this.beatMarkers, (ev: number) => ev === x);

      _.remove(this.beatMarkers, (ev: number, idx: number) => idx === index);
      if (this.isAppliedAI) {
        _.remove(this.originalHesBeatStatus, (ev: number, idx: number) => idx === index);
        _.remove(this.currentHesBeatStatus, (ev: number, idx: number) => idx === index);
      }
      _.remove(
        this.arrayBeatMarker,
        (x: BeatMarkerEntry) =>
          x.rectangle === beatMarker.rectangle &&
          x.triangle === beatMarker.triangle &&
          x.text === beatMarker.text,
      );
      this.canvas.remove(beatMarker.rectangle, beatMarker.triangle, beatMarker.text);

      this.draw(offsetX, false);
      this.render();
    }
  };

  BeatMarkersImpl.prototype.deleteBeatByObject = function (
    selectedBeatMarker: any,
    selectedBeatMarkerIndex: number,
    offsetX: number,
  ) {
    this.beatMarkers.splice(selectedBeatMarkerIndex, 1);
    this.currentHesBeatStatus.splice(selectedBeatMarkerIndex, 1);
    this.originalHesBeatStatus.splice(selectedBeatMarkerIndex, 1);
    this.handleOpenSnackbar('Beat deleted');
    this.reDrawBeatMarkers(offsetX, false);
    this.render();
  };

  BeatMarkersImpl.prototype.changeBeatType = function (
    selectedBeatMarker: any,
    selectedBeatMarkerIndex: number,
    beatTypeNumber: number,
    offsetX: number,
  ) {
    this.originalHesBeatStatus[selectedBeatMarkerIndex] = beatTypeNumber;
    this.currentHesBeatStatus[selectedBeatMarkerIndex] = beatTypeNumber;
    this.handleOpenSnackbar(`Change to ${convertBeatTypeNumberToText(beatTypeNumber)}-beat`);
    this.reDrawBeatMarkers(offsetX, false);
    this.render();
  };

  BeatMarkersImpl.prototype.setBeatMarkersVisibility = function (isVisibility: boolean) {
    this.isVisibility = isVisibility;

    this.arrayLabel.forEach((label: fabric.Text, index: number) => {
      if (index < this.sizeLabel) {
        label.visible = this.isVisibility;
      } else {
        label.visible = false;
      }
    });

    this.arrayBeatMarker.forEach((marker: BeatMarkerEntry, index: number) => {
      if (index < this.sizeBeatMarker) {
        marker.text.visible = this.isVisibility;
        marker.triangle.visible = this.isVisibility;
        marker.rectangle.visible = this.isVisibility;
      } else {
        marker.text.visible = false;
        marker.triangle.visible = false;
        marker.rectangle.visible = false;
      }
    });
  };

  BeatMarkersImpl.prototype.indexToPixOffset = function (index: number, offsetX: number) {
    const { indexToPix } = this.config;
    return (index - offsetX) * indexToPix;
  };

  BeatMarkersImpl.prototype.reDrawBeatMarkers = function (
    offsetX: number,
    isInViewAllMode: boolean,
  ) {
    this.arrayBeatMarker.forEach((beatMarker: BeatMarkerEntry) => {
      this.canvas.remove(beatMarker.rectangle, beatMarker.triangle, beatMarker.text);
    });
    this.arrayBeatMarker.length = 0;

    this.arrayLabel.forEach((label: fabric.Text) => {
      this.canvas.remove(label);
    });
    this.arrayLabel.length = 0;

    this.draw(offsetX, isInViewAllMode);
  };

  BeatMarkersImpl.prototype.applyAI = function (
    isAppliedAI: boolean,
    offsetX: number,
    isInViewAllMode: boolean,
    beatMarkers: number[] = [],
    hesBeatStatus: number[] = [],
    isShowVES: boolean,
    isShowSVES: boolean,
    aiData: any,
  ) {
    this.isAppliedAI = isAppliedAI;

    this.beatMarkers = [...beatMarkers];
    this.originalHesBeatStatus = [...hesBeatStatus];
    this.currentHesBeatStatus = [...hesBeatStatus];
    this.aiData = aiData;
    this.isShowVES = isShowVES;
    this.isShowSVES = isShowSVES;
    if (!isShowVES) {
      this.changeHesBeatStatus(60);
    }
    if (!isShowSVES) {
      this.changeHesBeatStatus(70);
    }
    this.reDrawBeatMarkers(offsetX, isInViewAllMode);
  };

  BeatMarkersImpl.prototype.calculateAIHeartRate = function () {
    const aiHeartRateArray: number[] = [];
    let sum = 0;
    if (this.aiData.beats.length && this.aiData.hesBeatStatus.length) {
      const beatsLength = this.aiData.beats.length;
      for (let i = 0; i < beatsLength - 1; i += 1) {
        const bpm = this.getBeatMarkerLabel(this.aiData.beats[i], this.aiData.beats[i + 1], false);
        sum += bpm;
        aiHeartRateArray.push(bpm);
      }
      const maxAIHeartRate = _.max(aiHeartRateArray);
      const minAIHeartRate = _.min(aiHeartRateArray);
      const averageAIHeartRate = Math.round(sum / this.aiData.beats.length);
      return { maxAIHeartRate, minAIHeartRate, averageAIHeartRate };
    }
    return { maxAIHeartRate: 0, minAIHeartRate: 0, averageAIHeartRate: 0 };
  };

  BeatMarkersImpl.prototype.getBeatMarkers = function () {
    return [...this.beatMarkers];
  };

  BeatMarkersImpl.prototype.getHesBeatStatus = function () {
    // return [...this.originalHesBeatStatus];
    return [...this.currentHesBeatStatus];
  };

  BeatMarkersImpl.prototype.changeHesBeatStatus = function (hes: number) {
    this.currentHesBeatStatus = this.currentHesBeatStatus.map((status: number) => {
      if (status === hes) {
        return 1;
      }
      return status;
    });
  };

  BeatMarkersImpl.prototype.toggleVES = function (offsetX: number, isInViewAllMode: boolean) {
    this.isShowVES = !this.isShowVES;
    if (this.isShowVES) {
      this.currentHesBeatStatus = [...this.originalHesBeatStatus];
      if (!this.isShowSVES) {
        this.changeHesBeatStatus(70);
      }
    } else {
      this.changeHesBeatStatus(60);
    }
    this.reDrawBeatMarkers(offsetX, isInViewAllMode);
    this.render();
  };

  BeatMarkersImpl.prototype.toggleSVES = function (offsetX: number, isInViewAllMode: boolean) {
    this.isShowSVES = !this.isShowSVES;
    if (this.isShowSVES) {
      this.currentHesBeatStatus = [...this.originalHesBeatStatus];
      if (!this.isShowVES) {
        this.changeHesBeatStatus(60);
      }
    } else {
      this.changeHesBeatStatus(70);
    }
    this.reDrawBeatMarkers(offsetX, isInViewAllMode);
    this.render();
  };

  BeatMarkersImpl.prototype.getJSONObject = function () {
    return {
      type: this.type,
      beatMarkers: this.beatMarkers,
    };
  };

  BeatMarkersImpl.prototype.checkBeatAtPosition = function (
    pointer: { x: number; y: number },
    offsetX: number,
  ) {
    // *: Find beat marker with point
    const selectedBeatMarker = _.find(
      this.arrayBeatMarker,
      (beat: BeatMarkerEntry) =>
        beat.rectangle.visible &&
        beat.triangle.visible &&
        beat.text.visible &&
        ((pointer.x >= beat.rectangle.left &&
          pointer.x <= beat.rectangle.left + beat.rectangle.width) ||
          // && pointer.y >= beat.rectangle.top && pointer.y <= beat.rectangle.top + beat.rectangle.height
          (pointer.x >= beat.triangle.left &&
            pointer.x <= beat.triangle.left + beat.triangle.width)),
      // && pointer.y >= beat.triangle.top && pointer.y <= beat.triangle.top + beat.triangle.height
    ); // *: Click rectangle or triangle
    if (selectedBeatMarker) {
      const x = Math.round(
        (selectedBeatMarker.rectangle.left + 4) / this.config.indexToPix + offsetX,
      );
      // *: Find index of found beat marker
      const selectedBeatMarkerIndex = _.findLastIndex(this.beatMarkers, (ev: number) => ev === x);
      return {
        selectedBeatMarker,
        selectedBeatMarkerIndex,
        selectedBeatMarkerSample: this.beatMarkers[selectedBeatMarkerIndex],
        selectedBeatTypeNumber: formatHesBeatStatus(
          this.currentHesBeatStatus[selectedBeatMarkerIndex],
        ),
      };
    }
    return null;
  };

  BeatMarkersImpl.prototype.getSelectedBulkBeatMarkers = function (
    startSample: number,
    endSample: number,
  ) {
    const filteredBulkBeatMarkers = this.beatMarkers.filter(
      (beat: number) => beat >= startSample && beat <= endSample,
    );
    if (filteredBulkBeatMarkers.length > 0) {
      const selectedBulkBeatMarkers: any[] = [];
      filteredBulkBeatMarkers.forEach((selectedBeatMarker: number) => {
        const selectedBeatMarkerIndex = _.findIndex(
          this.beatMarkers,
          (x: number) => x === selectedBeatMarker,
        );
        selectedBulkBeatMarkers.push({
          selectedBeatMarker,
          selectedBeatMarkerIndex,
          selectedBeatMarkerSample: this.beatMarkers[selectedBeatMarkerIndex],
        });
      });
      return selectedBulkBeatMarkers;
    }
    return [];
  };

  BeatMarkersImpl.prototype.deleteMultipleBeat = function ({
    startSample,
    endSample,
    offsetX,
  }: {
    startSample: number;
    endSample: number;
    offsetX: number;
  }) {
    const filteredDeleteBeatSample: number[] = [];
    const filteredDeleteBeatIndex: number[] = [];
    this.beatMarkers.forEach((beatSample: number, index: number) => {
      if (beatSample >= startSample && beatSample <= endSample) {
        filteredDeleteBeatSample.push(beatSample);
        filteredDeleteBeatIndex.push(index);
      }
    });
    this.beatMarkers = this.beatMarkers.filter(
      (ev: number, idx: number) => !filteredDeleteBeatIndex.includes(idx),
    );
    this.currentHesBeatStatus = this.currentHesBeatStatus.filter(
      (ev: number, idx: number) => !filteredDeleteBeatIndex.includes(idx),
    );
    this.originalHesBeatStatus = this.originalHesBeatStatus.filter(
      (ev: number, idx: number) => !filteredDeleteBeatIndex.includes(idx),
    );
    this.arrayBeatMarker.forEach((beat: BeatMarkerEntry) => {
      if (filteredDeleteBeatSample.includes(beat.beatSample)) {
        this.canvas.remove(beat.rectangle, beat.triangle, beat.text);
      }
    });
    if (!_.isEmpty(filteredDeleteBeatIndex)) {
      this.handleOpenSnackbar(
        filteredDeleteBeatIndex.length > 1
          ? `${filteredDeleteBeatIndex.length} beats deleted`
          : 'Beat deleted',
      );
    }
    this.reDrawBeatMarkers(offsetX, false);
    this.render();
  };

  BeatMarkersImpl.prototype.changeMultipleBeatType = function (
    startSample: number,
    endSample: number,
    offsetX: number,
    beatTypeNumber: number,
  ) {
    const selectedBulkBeatMarkers = this.getSelectedBulkBeatMarkers(startSample, endSample);
    if (selectedBulkBeatMarkers?.length > 0) {
      if (beatTypeNumber) {
        selectedBulkBeatMarkers.forEach((item: any) => {
          this.originalHesBeatStatus[item.selectedBeatMarkerIndex] = beatTypeNumber;
          this.currentHesBeatStatus[item.selectedBeatMarkerIndex] = beatTypeNumber;
        });
      } else {
        this.beatMarkers.splice(
          selectedBulkBeatMarkers[0].selectedBeatMarkerIndex,
          selectedBulkBeatMarkers.length,
        );
        this.originalHesBeatStatus.splice(
          selectedBulkBeatMarkers[0].selectedBeatMarkerIndex,
          selectedBulkBeatMarkers.length,
        );
        this.currentHesBeatStatus.splice(
          selectedBulkBeatMarkers[0].selectedBeatMarkerIndex,
          selectedBulkBeatMarkers.length,
        );
      }
      this.handleOpenSnackbar(
        selectedBulkBeatMarkers?.length > 1
          ? `${selectedBulkBeatMarkers.length} beats changed to ${convertBeatTypeNumberToText(beatTypeNumber)}-beat`
          : `Changed to ${convertBeatTypeNumberToText(beatTypeNumber)}-beat`,
      );
      this.reDrawBeatMarkers(offsetX, false);
      this.render();
    }
  };
})();

const BeatMarkers: BeatMarkersCtor = BeatMarkersImpl;

export { BeatMarkers };
export default { BeatMarkers };
